// Support agent inbox: all users' conversations, filterable by status, with a
// chat pane for replying and resolving. Route: /support-agent (behind
// ProtectedRoute); rendering is additionally gated on user.is_support_agent,
// and the backend enforces the role on every endpoint regardless.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Bell, BellOff, CheckCircle2, Inbox, Send } from 'lucide-react';
import { getApiUrl } from '../utils/apiHelpers';
import { useAgentSupport } from '../hooks/useSupport';
import { usePageVisible } from '../hooks/usePageVisible';
import { MessageStream, formatMessageDate } from './SupportPage';

const FILTERS = ['open', 'resolved', 'all'];
const POLL_INTERVAL_MS = 5000;

function SupportAgentInbox({ user }) {
  const apiUrl = getApiUrl();
  const {
    conversations, isLoading, error, fetchInbox, refreshInbox, fetchUpdates,
    fetchAgentConversation, agentReply, agentResolve,
    fetchNotificationSettings, updateNotificationSettings,
  } = useAgentSupport(apiUrl);
  const [filter, setFilter] = useState('open');
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadError, setThreadError] = useState('');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  // null = not loaded yet; the toggle renders once the current value is known.
  const [emailAlerts, setEmailAlerts] = useState(null);
  const [savingAlerts, setSavingAlerts] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkedRef = useRef(false);
  const visible = usePageVisible();
  const cursorRef = useRef(null);
  const pollBusyRef = useRef(false);
  const threadScrollRef = useRef(null);
  const prevThreadKeyRef = useRef(null);

  // On phones the message list is its own scroll container: jump to the
  // newest message when a thread opens, and follow new messages only while
  // already near the bottom. On md+ the container doesn't scroll (the page
  // does, as before), so this is a no-op there.
  useEffect(() => {
    const el = threadScrollRef.current;
    if (!thread || !el) return;
    const opened = prevThreadKeyRef.current !== selectedId;
    prevThreadKeyRef.current = selectedId;
    if (el.scrollHeight <= el.clientHeight + 4) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    if (opened || nearBottom) el.scrollTop = el.scrollHeight;
  }, [thread, selectedId]);

  useEffect(() => {
    fetchInbox(filter);
  }, [fetchInbox, filter]);

  // Realtime-ish updates: while the tab is visible, poll the cheap change
  // cursor every 5s (which also heartbeats agent presence for "email me when
  // away") and refetch the inbox — plus the open thread — only when it moves.
  useEffect(() => {
    if (!visible || !user?.is_support_agent) return;
    const tick = async () => {
      if (pollBusyRef.current || sending) return;
      pollBusyRef.current = true;
      try {
        const u = await fetchUpdates();
        const cursor = `${u.last_message_id}:${u.open_count}`;
        if (cursor !== cursorRef.current) {
          cursorRef.current = cursor;
          await refreshInbox(filter);
          if (selectedId) setThread(await fetchAgentConversation(selectedId));
        }
      } catch (_) {
        // Silent: keep the old cursor, the next tick retries.
      } finally {
        pollBusyRef.current = false;
      }
    };
    tick();
    const id = setInterval(tick, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [visible, user?.is_support_agent, filter, selectedId, sending, fetchUpdates, refreshInbox, fetchAgentConversation]);

  useEffect(() => {
    if (!user?.is_support_agent) return;
    fetchNotificationSettings()
      .then((data) => setEmailAlerts(!!data.email_on_new_request))
      .catch(() => setEmailAlerts(false));
  }, [user?.is_support_agent, fetchNotificationSettings]);

  const openThread = useCallback(async (id) => {
    setSelectedId(id);
    setThread(null);
    setThreadError('');
    setSearchParams({ conversation: String(id) }, { replace: true });
    try {
      setThread(await fetchAgentConversation(id));
      // Opening marks the thread read server-side; refresh the list so its
      // unread dot clears (the change cursor doesn't move on read marks).
      refreshInbox(filter).catch(() => {});
    } catch (e) {
      setThreadError(e.message);
    }
  }, [fetchAgentConversation, refreshInbox, filter, setSearchParams]);

  const closeThread = useCallback(() => {
    setSelectedId(null);
    setThread(null);
    setThreadError('');
    setSearchParams({}, { replace: true });
  }, [setSearchParams]);

  // Notification emails link straight to /support-agent?conversation=<id>;
  // open that thread on first render.
  useEffect(() => {
    if (deepLinkedRef.current || !user?.is_support_agent) return;
    deepLinkedRef.current = true;
    const linkedId = Number(searchParams.get('conversation'));
    if (linkedId) openThread(linkedId);
  }, [user?.is_support_agent, searchParams, openThread]);

  const toggleEmailAlerts = async () => {
    if (savingAlerts || emailAlerts === null) return;
    setSavingAlerts(true);
    try {
      const next = !emailAlerts;
      await updateNotificationSettings(next);
      setEmailAlerts(next);
    } catch (e) {
      // Keep the previous state; the next click retries.
    } finally {
      setSavingAlerts(false);
    }
  };

  const refreshAfterAction = useCallback(async (id) => {
    setThread(await fetchAgentConversation(id));
    await fetchInbox(filter);
  }, [fetchAgentConversation, fetchInbox, filter]);

  const handleReply = async (e) => {
    e.preventDefault();
    const body = draft.trim();
    if (!body || sending || !selectedId) return;
    setSending(true);
    setThreadError('');
    try {
      await agentReply(selectedId, body);
      setDraft('');
      await refreshAfterAction(selectedId);
    } catch (err) {
      setThreadError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleResolve = async () => {
    if (sending || !selectedId) return;
    setSending(true);
    setThreadError('');
    try {
      await agentResolve(selectedId);
      await refreshAfterAction(selectedId);
    } catch (err) {
      setThreadError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (!user?.is_support_agent) {
    return (
      <div className="min-h-screen bg-[var(--color-paper-bg)] text-[var(--color-ink-main)] flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-serif font-bold text-2xl mb-2">Not authorized</h1>
          <p className="text-sm text-[var(--color-ink-light)] mb-6">This page is for Steadily support agents.</p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] font-serif">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-paper-bg)] text-[var(--color-ink-main)]">
      <nav className="px-4 md:px-8 pt-6 pb-4 max-w-6xl mx-auto flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <div className="w-10 h-10 flex items-center justify-center bg-[var(--color-surface-card)] rounded-xl shadow-sm border border-[var(--color-divider)] p-1 transition-transform group-hover:scale-105">
            <img src="/android-chrome-192x192.png" alt="Steadily.me Logo" className="w-full h-full object-contain rounded-lg" />
          </div>
          <span className="font-serif font-bold text-xl">Support Inbox</span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors font-serif">
          <ArrowLeft className="w-4 h-4" />
          Back to home
        </Link>
      </nav>

      <main className="px-4 md:px-8 pb-16 max-w-6xl mx-auto">
        <div className="flex items-center justify-between gap-2 flex-wrap mt-4 mb-6">
          <div className="flex gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded-xl text-sm capitalize border transition-colors ${
                  filter === f
                    ? 'bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] border-transparent'
                    : 'bg-[var(--color-surface-card)] text-[var(--color-ink-light)] border-[var(--color-divider)] hover:text-[var(--color-ink-main)]'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          {emailAlerts !== null && (
            <button
              onClick={toggleEmailAlerts}
              disabled={savingAlerts}
              title={emailAlerts
                ? "New support requests are emailed to you while you're not viewing this inbox. Click to turn off."
                : "Get an email when a support request arrives while you're not viewing this inbox."}
              className={`inline-flex items-center gap-1.5 px-3 py-2 md:py-1.5 rounded-xl text-sm border transition-colors disabled:opacity-50 ${
                emailAlerts
                  ? 'bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] border-transparent'
                  : 'bg-[var(--color-surface-card)] text-[var(--color-ink-light)] border-[var(--color-divider)] hover:text-[var(--color-ink-main)]'
              }`}
            >
              {emailAlerts ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              {emailAlerts ? 'Email when away' : 'Email me when away'}
            </button>
          )}
        </div>

        {/* Master/detail: desktop (md+) shows list and thread side by side;
            phones show one at a time — the list, or the selected thread with
            a back button. */}
        <div className="grid md:grid-cols-[minmax(280px,380px)_1fr] gap-6 items-start">
          {/* Conversation list */}
          <div className={`bg-[var(--color-surface-card)] border border-[var(--color-divider)] rounded-2xl overflow-hidden ${selectedId ? 'hidden md:block' : ''}`}>
            {isLoading ? (
              <p className="text-sm text-[var(--color-ink-light)] p-6 text-center">Loading…</p>
            ) : error ? (
              <p className="text-sm text-red-600 p-6 text-center">{error}</p>
            ) : conversations.length === 0 ? (
              <div className="p-8 text-center">
                <Inbox className="w-8 h-8 mx-auto mb-3 text-[var(--color-ink-subtle)]" />
                <p className="text-sm text-[var(--color-ink-light)]">No {filter === 'all' ? '' : filter + ' '}conversations.</p>
              </div>
            ) : (
              <ul className="divide-y divide-[var(--color-divider)]">
                {conversations.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => openThread(c.id)}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        selectedId === c.id ? 'bg-[var(--color-paper-warm)]' : 'hover:bg-[var(--color-paper-warm)]'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="text-sm font-semibold truncate">{c.user_email}</span>
                        <span className="flex items-center gap-1.5 flex-shrink-0">
                          {c.unread && <span className="w-2 h-2 rounded-full bg-[var(--color-accent-warm)]" />}
                          <span className="text-[11px] text-[var(--color-ink-subtle)]">
                            {formatMessageDate(c.last_message_at || c.updated_at)}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs text-[var(--color-ink-light)] truncate">
                          {c.last_message_is_agent ? 'You: ' : ''}{c.last_message_preview || '—'}
                        </p>
                        <span className={`text-[10px] uppercase tracking-wide flex-shrink-0 ${
                          c.status === 'open' ? 'text-[var(--color-accent-warm)]' : 'text-[var(--color-ink-subtle)]'
                        }`}>
                          {c.status}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Thread pane. On phones it's a fixed-height column (scrolling
              messages, composer pinned at the bottom above the safe area);
              on md+ it stays the flowing card it always was. */}
          <div className={`bg-[var(--color-surface-card)] border border-[var(--color-divider)] rounded-2xl p-5 min-h-[300px] flex flex-col h-[calc(100dvh-13.5rem)] md:h-auto md:block ${selectedId ? '' : 'hidden md:block'}`}>
            {!selectedId ? (
              <p className="text-sm text-[var(--color-ink-light)] text-center py-16">Select a conversation.</p>
            ) : threadError && !thread ? (
              <p className="text-sm text-red-600 text-center py-16">{threadError}</p>
            ) : !thread ? (
              <p className="text-sm text-[var(--color-ink-light)] text-center py-16">Loading conversation…</p>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 pb-4 mb-4 border-b border-[var(--color-divider)]">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <button
                      onClick={closeThread}
                      aria-label="Back to conversations"
                      className="md:hidden p-2 -ml-2 flex-shrink-0 text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="min-w-0">
                      <p className="font-semibold text-sm truncate">{thread.conversation.user_email}</p>
                      <p className="text-[11px] text-[var(--color-ink-subtle)] capitalize">{thread.conversation.status}</p>
                    </div>
                  </div>
                  {thread.conversation.status === 'open' && (
                    <button
                      onClick={handleResolve}
                      disabled={sending}
                      className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-xl border border-[var(--color-divider)] text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors disabled:opacity-50"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Resolve
                    </button>
                  )}
                </div>

                <div ref={threadScrollRef} className="flex-1 overflow-y-auto min-h-0 md:flex-none md:overflow-visible">
                  <MessageStream
                    messages={thread.messages}
                    viewerIsAgent
                    userLabel={thread.conversation.user_email}
                  />
                </div>

                <form onSubmit={handleReply} className="mt-4 md:mt-6 pb-[max(0px,env(safe-area-inset-bottom))]">
                  {threadError && <p className="text-red-600 text-sm mb-2">{threadError}</p>}
                  <div className="flex items-end gap-2">
                    <textarea
                      value={draft}
                      onChange={(e) => setDraft(e.target.value.slice(0, 5000))}
                      placeholder="Reply as Steadily Support…"
                      rows={3}
                      className="flex-1 resize-none rounded-2xl border border-[var(--color-divider)] bg-[var(--color-surface-input)] px-4 py-3 text-[15px] placeholder-[var(--color-ink-subtle)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent-warm)]"
                    />
                    <button
                      type="submit"
                      disabled={!draft.trim() || sending}
                      className="inline-flex items-center gap-1.5 px-4 py-3 rounded-2xl bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] text-sm font-semibold disabled:opacity-40 transition-opacity"
                    >
                      <Send className="w-4 h-4" />
                      Reply
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default SupportAgentInbox;
